import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Table, Tag } from 'antd';
import { AppstoreOutlined, SearchOutlined, TableOutlined } from '@ant-design/icons';
import { useSearchReportsQuery, type SearchResult } from 'services/exploreService';
import { BarList, Empty, Kpi, PageHead, Panel, Skeleton, fmt, studyHref } from 'components/explore';

const EXAMPLES = [
  'aortic stenosis',
  'mitral regurgitation',
  'pericardial effusion',
  'reduced ejection fraction',
  'LV hypertrophy',
];

type StudyHit = SearchResult['studies'][number];

const VIA_LABEL: Record<string, string> = {
  coded: 'coded finding',
  finding: 'finding sentence',
  text: 'report text',
};

/** Highlight each query term, including whole quoted phrases, inside a snippet. */
const Highlight: React.FC<{ text: string; tokens: string[] }> = ({ text, tokens }) => {
  if (!tokens.length) return <>{text}</>;
  const re = new RegExp(
    `(${tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi'
  );
  return (
    <>
      {text.split(re).map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="ax-mark">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  const { currentData: data, isFetching, error } = useSearchReportsQuery(q, { skip: q.length < 2 });

  const submit = (value: string) => {
    const v = value.trim();
    if (v.length >= 2) setParams({ q: v });
  };

  const t = data?.totals ?? {};
  const textParam = `text=${encodeURIComponent(q)}`;

  const columns = [
    {
      title: 'Study',
      dataIndex: 'icid',
      key: 'icid',
      width: 200,
      render: (v: string | null, r: StudyHit) => (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Link to={studyHref(v ?? r.uuid)} className="ax-link">
            {v ?? r.uuid.slice(0, 8)}
          </Link>
          <span className="ax-faint" style={{ fontSize: 12 }}>
            {[r.datasource, r.visit_year].filter(Boolean).join(' · ') || '–'}
          </span>
          <span className="ax-faint ax-num" style={{ fontSize: 12 }}>
            {fmt(r.number_of_dicoms)} DICOMs
          </span>
        </span>
      ),
    },
    {
      title: 'Match',
      key: 'match',
      render: (_: unknown, r: StudyHit) => (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(r.via ?? []).map(v => (
              <Tag
                key={v}
                bordered={false}
                color={v === 'coded' ? 'blue' : undefined}
                style={{ marginInlineEnd: 0 }}
              >
                {VIA_LABEL[v] ?? v}
              </Tag>
            ))}
          </span>
          {r.snippet && (
            <span className="ax-muted">
              <Highlight text={r.snippet} tokens={data?.tokens ?? []} />
            </span>
          )}
          {(r.characterizations ?? []).length > 0 && (
            <span className="ax-faint" style={{ fontSize: 12 }}>
              Coded: {(r.characterizations ?? []).join(' · ')}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="ax-page">
      <PageHead
        title="Report search"
        lede={
          'Every word must appear in the report text or in a finding, including coded characterizations. Use double quotes for an exact phrase, e.g. "pulmonary regurgitation".'
        }
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input.Search
          size="large"
          style={{ maxWidth: 560 }}
          prefix={<SearchOutlined />}
          placeholder="e.g. aortic stenosis"
          aria-label="Search reports"
          value={draft}
          allowClear
          enterButton="Search"
          loading={isFetching}
          onChange={e => setDraft(e.target.value)}
          onSearch={submit}
        />
        <span className="ax-faint" style={{ fontSize: 12.5 }}>
          Try:{' '}
          {EXAMPLES.map((e, i) => (
            <React.Fragment key={e}>
              {i > 0 && ' · '}
              <button type="button" className="ax-textbtn" onClick={() => submit(e)}>
                {e}
              </button>
            </React.Fragment>
          ))}
        </span>
      </div>

      {q.length < 2 ? (
        <Panel>
          <Empty title="Search report findings and text">
            Type at least two letters, or pick an example above.
          </Empty>
        </Panel>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Search failed"
          description="The Explorer API returned an error."
        />
      ) : !data ? (
        <Skeleton h={200} />
      ) : (
        <>
          <section className="ax-tiles" aria-label="Match counts">
            <Kpi
              value={fmt(t.reports)}
              label="Matching reports"
              sub={`${fmt(t.reports_coded)} via coded findings`}
              color="var(--s1)"
            />
            <Kpi
              value={fmt(t.studies)}
              label="Studies"
              sub={`${fmt(t.patients)} patients`}
              color="var(--s1)"
              onClick={t.studies ? () => navigate(`/studies?${textParam}`) : undefined}
            />
            <Kpi
              value={fmt(t.dicoms)}
              label="DICOMs in those studies"
              color="var(--s3)"
              onClick={t.dicoms ? () => navigate(`/dicoms?${textParam}`) : undefined}
            />
            <Kpi
              value={fmt(t.reports_text)}
              label="Reports matched in text"
              sub="includes negations ('no …')"
              color="var(--line)"
            />
          </section>

          <div className="ax-grid-2">
            <Panel
              eyebrow="Coded characterizations"
              title="Reported severity"
              sub="Coded findings whose region, condition and severity match all search terms. Unlike text matches, these exclude negated statements. Click a bar to open those studies."
            >
              {data.by_characterization.length ? (
                <BarList
                  ariaLabel="Coded characterizations"
                  items={data.by_characterization.map(c => {
                    const finding = [c.region, c.condition, c.stratification].map(x => x ?? '').join('|');
                    return {
                      key: finding,
                      label: `${(c.condition ?? '').replace(/_/g, ' ')} · ${c.stratification ?? '–'}`,
                      hint: c.region ?? undefined,
                      value: c.reports,
                      secondary: `${fmt(c.studies)} st`,
                      tip: `${c.region} · ${(c.condition ?? '').replace(/_/g, ' ')} · ${c.stratification}: ${fmt(c.reports)} reports in ${fmt(c.studies)} studies. Click to open them.`,
                      onClick: () => navigate(`/studies?finding=${encodeURIComponent(finding)}`),
                    };
                  })}
                />
              ) : (
                <p className="ax-faint" style={{ margin: 0 }}>
                  No coded characterization matches all search terms.
                </p>
              )}
            </Panel>
            <Panel eyebrow="Sources" title="Matching studies by source">
              {data.by_source.length ? (
                <BarList
                  ariaLabel="Matching studies by source"
                  items={data.by_source.map(s => ({
                    key: s.id ?? s.name,
                    label: s.name,
                    value: s.studies,
                    onClick: s.id
                      ? () => navigate(`/studies?${textParam}&datasource=${s.id}`)
                      : undefined,
                  }))}
                />
              ) : (
                <p className="ax-faint" style={{ margin: 0 }}>
                  No matches.
                </p>
              )}
            </Panel>
          </div>

          <Panel
            eyebrow="Matches"
            title={`${fmt(data.studies.length)}${(t.studies ?? 0) > data.studies.length ? ` of ${fmt(t.studies)}` : ''} matching studies`}
            actions={
              <span style={{ display: 'flex', gap: 8 }}>
                <Button
                  icon={<TableOutlined />}
                  disabled={!t.studies}
                  onClick={() => navigate(`/studies?${textParam}`)}
                >
                  Browse studies
                </Button>
                <Button
                  icon={<AppstoreOutlined />}
                  disabled={!t.dicoms}
                  onClick={() => navigate(`/dicoms?${textParam}`)}
                >
                  Browse DICOMs
                </Button>
              </span>
            }
          >
            <Table<StudyHit>
              className="ax-table"
              size="small"
              rowKey="uuid"
              columns={columns}
              dataSource={data.studies}
              pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <Empty title={`No reports mention "${q}"`}>Try fewer or different words.</Empty>
                ),
              }}
              onRow={r => ({ onClick: () => navigate(studyHref(r.icid ?? r.uuid)) })}
            />
          </Panel>
        </>
      )}
    </div>
  );
};

export default Search;
